import { LoaderCircle, Minus, Plus, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { WorkingPlanResponse } from "@rnavoyager/contracts";

import { getTaskPlanRevision } from "./api";
import { comparePlans, dependencyTitles } from "./plan-changes";
import type { PlanRevisionMarker } from "./plan-changes";
import "./TaskPlanChanges.css";

const PAGE_SIZE = 10;
const STATUS_LABELS = {
  pending: "待执行",
  in_progress: "执行中",
  completed: "已完成",
  failed: "失败",
  cancelled: "已取消",
};

export function TaskPlanChanges({
  sessionId,
  turnId,
  markers,
}: {
  sessionId: string;
  turnId: string;
  markers: PlanRevisionMarker[];
}) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [snapshots, setSnapshots] = useState(new Map<number, WorkingPlanResponse>());
  const [errors, setErrors] = useState(new Map<number, string>());
  const [loading, setLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  const cache = useRef(new Map<number, WorkingPlanResponse>());
  const signature = markers
    .map(({ revision, planId, digest }) => `${revision}:${planId}:${digest}`)
    .join("|");
  const visible = useMemo(() => markers.slice(0, visibleCount), [markers, visibleCount]);

  useEffect(() => {
    let disposed = false;
    const load = async () => {
      setLoading(true);
      const failures = new Map<number, string>();
      const required = [
        ...new Set(
          visible.flatMap(({ revision }) =>
            revision === 0 ? [revision] : [revision, revision - 1],
          ),
        ),
      ];
      // 限制并发；只有打开改动记录时才读取历史，更多版本由用户按页展开。
      for (let offset = 0; offset < required.length && !disposed; offset += 3) {
        await Promise.all(
          required.slice(offset, offset + 3).map(async (revision) => {
            const marker = markers.find((item) => item.revision === revision);
            try {
              const plan =
                cache.current.get(revision) ?? (await getTaskPlanRevision(turnId, revision));
              if (
                plan.turnId !== turnId ||
                plan.revision !== revision ||
                plan.sessionId !== sessionId ||
                (marker && (plan.planId !== marker.planId || plan.digest !== marker.digest))
              ) {
                throw new Error("规划版本与改动记录不一致");
              }
              if (!disposed) cache.current.set(revision, plan);
            } catch (cause) {
              failures.set(revision, cause instanceof Error ? cause.message : "无法读取规划版本");
            }
          }),
        );
        if (!disposed) setSnapshots(new Map(cache.current));
      }
      if (!disposed) {
        setErrors(failures);
        setLoading(false);
      }
    };
    void load();
    return () => {
      disposed = true;
    };
  }, [sessionId, turnId, signature, visibleCount, retry]);

  return (
    <section aria-label="任务改动" className="task-plan-changes">
      <p className="task-plan-changes-intro">按时间查看规划改动与执行进度。</p>
      {markers.length === 0 && <p className="workspace-action-empty">暂无规划改动。</p>}
      <ol className="task-plan-change-list">
        {visible.map((marker) => {
          const after = snapshots.get(marker.revision);
          const before = marker.revision > 0 ? snapshots.get(marker.revision - 1) : undefined;
          const failure = errors.get(marker.revision) ?? errors.get(marker.revision - 1);
          return (
            <li className="task-plan-revision" key={marker.revision}>
              <header>
                <strong>
                  {marker.revision === 0
                    ? "初始规划"
                    : `v${marker.revision - 1} → v${marker.revision}`}
                </strong>
                <time dateTime={marker.timestamp}>
                  {new Date(marker.timestamp).toLocaleString("zh-CN", {
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </time>
              </header>
              {failure ? (
                <p className="task-plan-change-error">无法比较本次改动：{failure}</p>
              ) : !after || (marker.revision > 0 && !before) ? (
                <p className="workspace-action-empty">正在读取规划版本…</p>
              ) : marker.revision === 0 ? (
                <ul className="task-plan-diff">
                  {after.steps.map((step, index) => (
                    <li className="added" key={step.stepId}>
                      <Plus aria-hidden="true" size={13} />
                      <span>
                        {index + 1}. {step.title}
                      </span>
                    </li>
                  ))}
                  {after.steps.length === 0 && <li>尚未列出步骤。</li>}
                </ul>
              ) : (
                <PlanRevisionDifference before={before!} after={after} />
              )}
            </li>
          );
        })}
      </ol>
      {loading && (
        <p aria-live="polite" className="task-plan-history-loading">
          <LoaderCircle aria-hidden="true" className="spin" size={14} />
          正在读取改动记录…
        </p>
      )}
      {errors.size > 0 && !loading && (
        <button
          className="task-plan-history-more"
          onClick={() => setRetry((value) => value + 1)}
          type="button"
        >
          <RefreshCw size={13} />
          重新读取
        </button>
      )}
      {markers.length > visibleCount && (
        <button
          className="task-plan-history-more"
          disabled={loading}
          onClick={() => setVisibleCount((value) => value + PAGE_SIZE)}
          type="button"
        >
          加载更早改动
        </button>
      )}
    </section>
  );
}

function PlanRevisionDifference({
  before,
  after,
}: {
  before: WorkingPlanResponse;
  after: WorkingPlanResponse;
}) {
  const difference = comparePlans(before, after);
  const structural =
    difference.added.length + difference.removed.length + difference.adjusted.length;
  return (
    <>
      {(structural > 0 || difference.progress.length === 0) && (
        <p className="task-plan-change-summary">
          {structural === 0
            ? "步骤内容未变化"
            : [
                difference.added.length > 0 && `新增 ${difference.added.length} 项`,
                difference.removed.length > 0 && `删除 ${difference.removed.length} 项`,
                difference.adjusted.length > 0 && `调整 ${difference.adjusted.length} 项`,
              ]
                .filter(Boolean)
                .join(" · ")}
        </p>
      )}
      {structural > 0 && (
        <ul className="task-plan-diff">
          {difference.removed.map(({ step, index }) => (
            <li className="removed" key={step.stepId}>
              <Minus aria-hidden="true" size={13} />
              <span>
                <small>删除</small>
                {index + 1}. {step.title}
              </span>
            </li>
          ))}
          {difference.added.map(({ step, index }) => (
            <li className="added" key={step.stepId}>
              <Plus aria-hidden="true" size={13} />
              <span>
                <small>新增</small>
                {index + 1}. {step.title}
              </span>
            </li>
          ))}
          {difference.adjusted.map((change) => (
            <li className="adjusted" key={change.after.stepId}>
              <RefreshCw aria-hidden="true" size={12} />
              <span>
                <strong>{change.after.title}</strong>
                {change.order && (
                  <small>
                    顺序：第 {change.previousIndex + 1} 步 → 第 {change.index + 1} 步
                  </small>
                )}
                {change.acceptance && (
                  <small>
                    验收：{change.before.acceptanceCriteria || "未设置"} →{" "}
                    {change.after.acceptanceCriteria || "未设置"}
                  </small>
                )}
                {change.dependencies && (
                  <small>
                    依赖：{dependencyTitles(change.before, before).join("、") || "无"} →{" "}
                    {dependencyTitles(change.after, after).join("、") || "无"}
                  </small>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {difference.progress.length > 0 && (
        <ul aria-label="执行状态变化" className="task-plan-progress-diff">
          {difference.progress.map((change, index) => (
            <li key={index}>
              <span>{change.title}</span>
              <small>
                {STATUS_LABELS[change.before]} → {STATUS_LABELS[change.after]}
              </small>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
